import React, { useState, useEffect, useRef } from 'react';
import ScrollAnimate from "./ScrollAnimate";
import { Link } from 'react-router-dom';
import ProgressBorder from './ProgressBorder';

const modulesPath = ["introduction", "videos", "resources", "assignments"];

// Hero Section for My Courses
const MyCoursesHero = ({ completedModules = 2, totalModules = 8, courseCompleted = false }) => {
  const progressTitles = {
    0: "Your journey starts here 🚀",
    1: "Great start! Keep it going ✨",
    2: "Nice progress! You're building momentum 🔥",
    3: "You're almost halfway there 💪",
    4: "Halfway champion 🏆",
    5: "Strong progress! Keep pushing 🚴",
    6: "Almost there—finish line in sight 🎯",
    7: "One step away from mastery ⭐",
    8: "Congratulations! You've completed the course 🎉",
  };

  return (
    <ScrollAnimate>
      <section className="hero">
        <h1>{progressTitles[completedModules]}</h1>
        <div className="terminal-bar">
          &gt; 🚀 {completedModules} modules down, {totalModules - completedModules} to go.
          <span className="blinking-cursor"></span>
        </div>
        <br />

        <a className="cta-primary" href="#modules">See Your Progress</a>
        {courseCompleted && (
          <Link className="cta-primary" to="/my-certificate">See Certificate</Link>
        )}
      </section>
    </ScrollAnimate>
  );
};

// Badge Strip Component
const BadgeStrip = ({ earnedBadges, totalBadges = 8 }) => {
  const badges = [
    { id: 1, name: 'Module 1 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-1.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-1.svg" },
    { id: 2, name: 'Module 2 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-2.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-2.svg" },
    { id: 3, name: 'Module 3 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-3.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-3.svg" },
    { id: 4, name: 'Module 4 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-4.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-4.svg" },
    { id: 5, name: 'Module 5 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-5.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-5.svg" },
    { id: 6, name: 'Module 6 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-6.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-6.svg" },
    { id: 7, name: 'Module 7 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-7.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-7.svg" },
    { id: 8, name: 'Module 8 Badge', icon: 'https://cdn.hackernoon.com/courses/svg/module-8.svg', lockedIcon: "https://cdn.hackernoon.com/courses/svg/locked-module-8.svg" },
  ];

  const getBadgeClass = (index) => {
    if (index < earnedBadges) return 'badge-earned';
    if (index === earnedBadges) return 'badge-current';
    // return 'badge-locked';
  };

  const getBadgeIcon = (index) => {
    if (index < earnedBadges) return badges[index]?.icon;
    if (index === earnedBadges) return badges[index]?.icon;
    return badges[index]?.lockedIcon;
  };

  return (
    <div className="badge-strip">
      {badges?.map((badge, index) => (
        <img
          key={badge?.id}
          src={getBadgeIcon(index)}
          alt={badge?.name}
          className={getBadgeClass(index)}
        />
      ))}
    </div>
  );
};

// Progress Section Component
const ProgressSection = ({ modules }) => {
  const [progressData, setProgressData] = useState({
    completed: 0,
    total: 0,
    percentage: 0
  });

  useEffect(() => {
    const completedModules = modules?.filter(m => m?.completed === true)?.length;
    const totalModules = modules?.length;
    const percentage = Math.round((completedModules / totalModules) * 100);

    setProgressData({
      completed: completedModules,
      total: totalModules,
      percentage
    });
  }, [modules]);

  const getBadgeText = () => {
    const { completed } = progressData;
    if (completed === 0) return "Ready to start your journey!";
    if (completed === 1) return "Great start! First module completed!";
    if (completed === 2) return "Awesome! You're building momentum!";
    if (completed === 3) return "Halfway there! Keep it up!";
    if (completed === 4) return "Fantastic progress! Over halfway!";
    if (completed === 5) return "Amazing! Almost there!";
    if (completed === 6) return "Incredible! Just two more to go!";
    if (completed === 7) return "So close! One module left!";
    if (completed === 8) return "Congratulations! Course completed! 🎉";
    return "Keep going!";
  };

  useEffect(() => {
    // Animate progress bar
    const timer = setTimeout(() => {
      const fillElement = document.getElementById('myProgressFill');
      if (fillElement) {
        fillElement.style.width = `${progressData?.percentage}%`;
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [progressData.percentage]);

  // console.log("progressData", progressData)

  return (
    <ScrollAnimate>
      <div className="progress-container" id="progress">
        <div className="progress-left">
          <div className="progress-title">My Progress</div>
          <div className="progress-bar">
            <div className="progress-fill" id="myProgressFill" style={{ width: '0%' }}></div>
          </div>
          <div className="progress-metrics" id="myProgressText">
            {progressData?.completed} of {progressData?.total} modules completed ({progressData?.percentage}%)
          </div>
        </div>

        <div className="progress-right">
          <BadgeStrip earnedBadges={progressData?.completed} />
          <div id="badgeText" className="badge-text">{getBadgeText()}</div>
        </div>
      </div>
    </ScrollAnimate>
  );
};

// Share Menu Component
const ShareMenu = ({ moduleNumber, moduleName, completed = false }) => {

  let text = `I just completed Module ${moduleNumber}: ${moduleName} in the @HackerNoon Blogging Course! 🚀`;
  const url = "https://courses.hackernoon.com";

  if (completed) {
    text = `I just completed HackerNoon Blogging Course! LEST GOOOO 🚀`;
  }

  const shareUrls = {
    'x/twitter': `https://twitter.com/intent/tweet?text=${text}`,
    'facebook': `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
    'linkedin': `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`
  };

  const handleShare = (platform) => {
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="share-wrapper">
      <button className="module-btn share" data-module={moduleNumber}>Share</button>
      <div className="share-menu">
        <a href="#" className="share-link twitter" onClick={(e) => { e.preventDefault(); handleShare('x/twitter'); }}>
          <svg id="x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="m15.5,10v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h-3v1h-1v1h-1v1h-1v1h-1v1h-1v1h-2v-1h-1v-1h-1v-2h-1v-1h-1v-1H1.5v1h1v1h1v1h1v2h1v1h1v2h1v1h1v2h1v1h-1v1h-1v1h-1v1h-1v1h-1v1h-1v1h-1v1h-1v1h3v-1h1v-1h1v-1h1v-1h1v-1h1v-1h2v1h1v1h1v2h1v1h1v1h7v-1h-1v-1h-1v-1h-1v-2h-1v-1h-1v-2h-1v-1h-1v-2h-1v-1h1Zm0,4v1h1v2h1v1h1v2h-3v-2h-1v-1h-1v-1h-1v-2h-1v-1h-1v-1h-1v-2h-1v-1h-1v-2h-1v-1h-1v-2h3v1h1v2h1v1h1v2h1v1h1v1h1v2h1Z" />
          </svg>
          X/Twitter
        </a>
        <a href="#" className="share-link facebook" onClick={(e) => { e.preventDefault(); handleShare('facebook'); }}>
          <svg id="facebook-round" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <polygon points="23 9 23 15 22 15 22 17 21 17 21 19 20 19 20 20 19 20 19 21 17 21 17 22 15 22 15 23 14 23 14 15 16 15 16 14 17 14 17 12 14 12 14 9 15 9 15 8 17 8 17 5 13 5 13 6 11 6 11 8 10 8 10 12 7 12 7 15 10 15 10 23 9 23 9 22 7 22 7 21 5 21 5 20 4 20 4 19 3 19 3 17 2 17 2 15 1 15 1 9 2 9 2 7 3 7 3 5 4 5 4 4 5 4 5 3 7 3 7 2 9 2 9 1 15 1 15 2 17 2 17 3 19 3 19 4 20 4 20 5 21 5 21 7 22 7 22 9 23 9" />
          </svg>
          Facebook
        </a>
        <a href="#" className="share-link linkedin" onClick={(e) => { e.preventDefault(); handleShare('linkedin'); }}>
          <svg id="linkedin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="m22,2v-1H2v1h-1v20h1v1h20v-1h1V2h-1Zm-9,10v8h-3v-11h3v1h1v-1h4v1h1v10h-3v-8h-3Zm-9-4v-3h3v3h-3Zm3,1v11h-3v-11h3Z" />
          </svg>
          LinkedIn
        </a>
      </div>
    </div>
  );
};

// Module Card Component
const ModuleCard = ({ module, canStart = false, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusBadge = () => {
    if (module?.progress === 100) {
      return <div className="module-badge done">{module?.completed ? "Done" : "Almost Done"}</div>;
    } else if (module?.progress > 0) {
      return <div className="module-badge progress">{module?.progress}% finished</div>;
    }
    return <div className="module-badge progress">Not started</div>;
  };

  const getButtonText = () => {
    if (module?.progress === 100) return module?.completed ? "Review Module" : " Finish Module";
    if (module?.progress > 0) return "Continue Module";
    return "";
  };

  const progressPercentage = (module?.progress / 100) * 400;

  return (
    <div
      className={`module-card ${(module?.progress === 0 && !canStart) ? 'not-started' : ''} ${isExpanded ? 'expanded' : ''}`}
      data-progress={module.progress}
      onClick={(e) => {
        if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'A') {
          setIsExpanded(!isExpanded);
        }
      }}
    >
      <ProgressBorder progress={module.progress} borderRadius={18} strokeWidth={1} />

      <div className="module-status">
        {getStatusBadge()}
        <div className="module-icon" dangerouslySetInnerHTML={{ __html: module?.svgIcon }} />
      </div>

      <div className="module-content">
        <div className="module-title">{module?.title}</div>
        <div className="module-desc">{module?.subTitle}</div>

        {module?.progress > 0 && (
          <Link to={`${module?._id}`} className="module-btn">{getButtonText()}</Link>
        )}
        {module?.completed && (
          <ShareMenu moduleNumber={index + 1} moduleName={module.title} />
        )}
        {(module?.progress === 0 && canStart) && (
          <>
            <Link to={`${module?._id}`} className="module-btn">Start Module</Link>
          </>
        )}
      </div>
    </div>
  );
};

// Modules Section
const ModulesSection = ({ modules }) => {

  const shouldModuleStart = (i) => {
    if (i - 1 < 0) { return true; }
    const prevModule = modules[i - 1];
    const currentModule = modules[i + 1];
    if (prevModule?.completed && !currentModule?.started) { return true; }
    return false;
  }

  return (
    <ScrollAnimate>
      <section className="modules-section" id="modules">
        <div className="modules-title">Course Modules</div>
        <div className="modules-grid">
          {modules?.map((module, i) => (
            <ModuleCard key={module?._id} module={module} canStart={shouldModuleStart(i)} index={i} />
          ))}
        </div>
      </section>
    </ScrollAnimate>
  )
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isActive, setIsActive] = useState(false);

  return (
    <div className="faq-item">
      <div className="faq-question" onClick={() => setIsActive(!isActive)}>
        <span>{question}</span>
        <span className={`faq-toggle ${isActive ? 'active' : ''}`}>+</span>
      </div>
      <div className={`faq-answer ${isActive ? 'active' : ''}`}>
        <div className="faq-answer-content" dangerouslySetInnerHTML={{ __html: answer }} />
      </div>
    </div>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What's the flow for buying and accessing the blogging course?",
      answer: `If you don't have a HackerNoon account, no worries. After the purchase, we'll automatically create your account, log you in, add the course to your profile, and take you to the receipt page.      
      <br><br>
      If you have a HackerNoon account, please log in first, then you can buy the course. Once purchased, we will add the course to your account and take you straight to the receipt page.`
    },
    {
      question: "Do I need to be an experienced writer to join?",
      answer: `Not at all. This course is built for people in tech who want to start writing, even if they've never published before. Many fellows have gone from zero to published authors in just weeks using the same exercises that grew HackerNoon's 4M+ reader base.
      <br><br>
      At the same time, it's also valuable for seasoned professionals - like cybersecurity experts, software engineers, and even PhDs - who already write but want to boost their blog quality, consistency, and impact.`
    },
    {
      question: "How long does it take to complete the course?",
      answer: "Most students complete the 8 modules in 6-8 weeks, but since it's self-paced, you can go faster or slower. You have lifetime access, so there's no rush. Many students start seeing career benefits after publishing just their first few articles."
    },
    {
      question: "What if I don't have time to blog regularly?",
      answer: "The course teaches you how to blog efficiently. Many successful students publish just once or twice a month. It's about consistency and quality, not quantity. Even publishing quarterly can significantly impact your career trajectory."
    },
    {
      question: "Will I really get published on HackerNoon?",
      answer: "Yes! Course graduates have published over 850 articles on HackerNoon. Our editorial team helps ensure your work meets publication standards. Plus, you'll learn the insider secrets that get articles featured and promoted to our 4M+ monthly readers."
    },
    {
      question: "How is this different from other writing courses?",
      answer: "Most writing courses are generic. This is specifically for people in tech, taught by the team that built one of the world's largest tech publications. You're not just learning to write - you're learning to build your career through strategic content creation."
    },
    {
      question: "What if I'm not technical enough?",
      answer: "If you work in tech - whether you're a developer, designer, product manager, or in sales/marketing at a tech company - you have valuable insights to share. The course shows you how to identify and communicate your unique perspective, regardless of your technical depth."
    },
    {
      question: "I cannot afford the course. Do you offer financial aid?",
      answer: `Yes! Students with verified status qualify for a 50% discount. To get verified, you'll need to fill out <a href="https://financial-aid-student-hackernoon-blogging-course.paperform.co/" target="_blank" rel="noopener noreferrer">this form</a> and provide proof such as a school-issued email address, valid student ID card, enrollment/acceptance letter, transcript, tuition receipt, ISIC card, or other official documentation.
      You'll pay $149 upfront (50% off), and once we confirm your status, you'll receive an access code to unlock the course.
      <br><br>
      Not a student? You can still apply for financial aid. Please go through <a href="https://financial-aid-non-student-hackernoon-blogging-course.paperform.co/" target="_blank" rel="noopener noreferrer">this form</a> to submit as a non-student, and you'll need to be more thorough in your responses — explain why you deserve support, how the course will help your career or personal growth, and why the discounted rate is necessary for you to participate.
      <br><br>
      As part of the discount program, you'll also be asked to submit a HackerNoon story using our <a href="https://app.hackernoon.com/blogging-templates" target="_blank" rel="noopener noreferrer">blogging templates</a>. This helps ensure active engagement in the community while accessing the discounted rate.
      `
    },
    {
      question: "What's the refund policy?",
      answer: `You can request a full refund within 7 days of purchase if you decide the program isn't for you. To be eligible, please submit your refund request within this period via this form.
      <br><br>
      Please note that participants who receive a refund will not be eligible for a course certificate and will lose access to the private community. Refunds are not available after the 7-day period has passed.`
    },
    {
      question: "Can blogging regularly really boost my career and help me become an expert in my field?",
      answer: "Yes. Writing consistently builds your internet reputation, increases your visibility, and creates compound career momentum. Many of our students earn promotions, speaking invites, consulting work, and become the go-to voices in their niches simply by publishing their ideas. Your expertise becomes known — and that becomes your advantage."
    }
  ];

  return (
    <ScrollAnimate>
      <section className="faq" id="faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-items">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>
    </ScrollAnimate>
  );
};

export { MyCoursesHero, ProgressSection, ModulesSection, FAQSection, ShareMenu }